import React, {
  createContext,
  ReactNode,
  useContext,
  useState,
  useCallback,
  SetStateAction,
  Dispatch,
  useEffect,
} from 'react';
import useStickyState from 'hooks/useStickyState';

type TrackChangesContextShape = {
  trackChangesState: boolean;
  setTrackChangesState: (value: boolean) => void;
  getGlobalTrackChangesState: () => boolean;
  showChanges: boolean;
  setShowChanges: Dispatch<SetStateAction<boolean>>;
};

let globalTrackChangesState = false;
let currentShowState = false;

const TrackChangesContext = createContext<TrackChangesContextShape | undefined>(undefined);

export const SharedTrackChangesContext = ({ children }: { children: ReactNode }): JSX.Element => {
  const [trackChangesState, setTrackChangesState] = useState<boolean>(true);
  const [showChanges, setShowChanges] = useStickyState<boolean>('phx-show-tracked-changes', false);

  const updateTrackChangesState = useCallback((value: boolean) => {
    globalTrackChangesState = value;
    setTrackChangesState(value);
  }, []);

  const getGlobalTrackChangesState = useCallback(() => globalTrackChangesState, []);
  const getCurrentShowChangesState = useCallback(() => currentShowState, []);

  // Update currentShowState whenever showChanges changes
  useEffect(() => {
    currentShowState = showChanges;
  }, [showChanges]);

  return (
    <TrackChangesContext.Provider
      value={{
        trackChangesState,
        setTrackChangesState: updateTrackChangesState,
        getGlobalTrackChangesState,
        showChanges,
        setShowChanges,
      }}
    >
      {children}
    </TrackChangesContext.Provider>
  );
};

export const useTrackChanges = (): TrackChangesContextShape => {
  const context = useContext(TrackChangesContext);
  if (!context) {
    throw new Error('useTrackChanges must be used within a SharedTrackChangesContext');
  }
  return context;
};

export const getGlobalTrackChangesState = () => globalTrackChangesState;
export const getCurrentShowChangesState = () => currentShowState;
